import { API_BASE_URL } from "../configuration/config";
import { BaseService } from "./base-service";

const CACHE_TTL_MS = 4 * 60 * 60 * 1000; // 4 hours
const PAGE_LIMIT = 500;

interface CatalogicalPaginatedResponse<T> {
  data: T[];
  pagination: { total: number; offset: number; limit: number };
}

interface CacheEntry<T> {
  data: T[];
  timestamp: number;
}

const BUILDINGS_ENDPOINT = `${API_BASE_URL}/cat-hworderbuildings`;
const FINCODES_ENDPOINT = `${API_BASE_URL}/cat-hworderfincodes`;

/**
 * Service for fetching config data (buildings, finance codes) from Catalogical.
 * Paginates sequentially and caches in localStorage with a 4-hour TTL.
 */
class CatalogicalConfigService extends BaseService {
  private memoryCache = new Map<string, CacheEntry<any>>();
  private inflightRequests = new Map<string, Promise<any[]>>();

  private getCacheKey(domain: string): string {
    return `catalogical-config:${domain}`;
  }

  private getFromCache<T>(domain: string): T[] | null {
    const key = this.getCacheKey(domain);

    // Check memory cache first
    const mem = this.memoryCache.get(key);
    if (mem && Date.now() - mem.timestamp < CACHE_TTL_MS) {
      return mem.data;
    }

    // Check localStorage
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const entry: CacheEntry<T> = JSON.parse(raw);
        if (Date.now() - entry.timestamp < CACHE_TTL_MS) {
          this.memoryCache.set(key, entry);
          return entry.data;
        }
        localStorage.removeItem(key);
      }
    } catch (e) {
      console.warn("[CatalogicalConfigService] localStorage read failed:", e);
    }

    return null;
  }

  private setCache<T>(domain: string, data: T[]): void {
    const key = this.getCacheKey(domain);
    const entry: CacheEntry<T> = { data, timestamp: Date.now() };
    this.memoryCache.set(key, entry);
    try {
      localStorage.setItem(key, JSON.stringify(entry));
    } catch (e) {
      console.warn("[CatalogicalConfigService] localStorage write failed:", e);
    }
  }

  /**
   * Fetches all items for a domain with pagination. Returns cached data if available.
   * Deduplicates concurrent in-flight requests to the same domain.
   */
  async getItems<T>(domain: string): Promise<T[]> {
    const cached = this.getFromCache<T>(domain);
    if (cached) return cached;

    const key = this.getCacheKey(domain);
    if (this.inflightRequests.has(key)) {
      return this.inflightRequests.get(key)! as Promise<T[]>;
    }

    const request = (async () => {
      const allItems: T[] = [];
      let offset = 0;
      let total = Infinity;

      while (offset < total) {
        const response = await this.fetch(`/items/${domain}/?limit=${PAGE_LIMIT}&offset=${offset}`);
        if (!response.ok) {
          throw new Error(`Failed to fetch ${domain}: ${response.status}`);
        }
        const body: CatalogicalPaginatedResponse<T> = await response.json();
        allItems.push(...body.data);
        total = body.pagination.total;
        offset += PAGE_LIMIT;
      }

      return allItems;
    })();

    this.inflightRequests.set(key, request);
    try {
      const result = await request;
      this.setCache(domain, result);
      return result;
    } finally {
      this.inflightRequests.delete(key);
    }
  }
}

export const buildingsConfigService = new CatalogicalConfigService(BUILDINGS_ENDPOINT);
export const fincodesConfigService = new CatalogicalConfigService(FINCODES_ENDPOINT);
